import { getManhattanDistance } from "utils/math/geometry/getManhattanDistance";
import { FindClosestEdgeInSet, EdgeMatchResult } from "./types";

/**
 * Finds the nearest edge to a point, but ONLY if both nodes of the edge
 * are present in the allowed set (e.g., a specific graph component).
 * Uses Manhattan distance for the metric.
 */
export const findClosestEdgeInSet: FindClosestEdgeInSet = ({
  point,
  topology,
  allowedNodeIds,
}) => {
  let minDist = Infinity;
  let best: EdgeMatchResult | null = null;

  const { x: px, y: py } = point;

  for (const edge of topology.edges) {
    if (!allowedNodeIds.has(edge.from) || !allowedNodeIds.has(edge.to)) {
      continue;
    }

    const nodeA = topology.nodes[edge.from];
    const nodeB = topology.nodes[edge.to];

    if (!nodeA || !nodeB) continue;

    const x1 = nodeA.x;
    const y1 = nodeA.y;
    const x2 = nodeB.x;
    const y2 = nodeB.y;

    const dx = x2 - x1;
    const dy = y2 - y1;

    // Skip zero-length segments
    if (dx === 0 && dy === 0) continue;

    // Vector projection logic
    const denom = dx * dx + dy * dy;
    const rawT = ((px - x1) * dx + (py - y1) * dy) / denom;
    const t = Math.max(0, Math.min(1, rawT));

    const projX = x1 + t * dx;
    const projY = y1 + t * dy;

    const dist = getManhattanDistance({
      pointA: point,
      pointB: { x: projX, y: projY },
    });

    if (dist < minDist) {
      minDist = dist;
      best = {
        segment: {
          fromNodeId: edge.from,
          toNodeId: edge.to,
        },
        projection: {
          projectedPoint: { x: projX, y: projY },
          routeCorner: { x: px, y: projY },
          distance: dist,
        },
      };
    }
  }

  return best;
};
